import React from 'react';
import {
  FaMicrochip,
  FaJava,
  FaPython,
  FaTerminal,
  FaExchangeAlt,
  FaRaspberryPi,
} from 'react-icons/fa';
import {
  SiCplusplus,
  SiC,
  SiTensorflow,
  SiKeras,
  SiOpencv,
  SiPlatformio,
  SiCmake,
  SiNvidia,
  SiArduino,
  SiEspressif,
  SiOpengl,
} from 'react-icons/si';
import {
  TbClockBolt,
  TbCircuitGround,
  TbMathFunction,
  TbNetwork,
  TbCpu,
} from 'react-icons/tb';
import useScrollReveal from '../../hooks/useScrollReveal';

const skillGroups = [
  {
    title: 'Languages',
    items: [
      { name: 'C++', icon: SiCplusplus },
      { name: 'C', icon: SiC },
      { name: 'Python', icon: FaPython },
      { name: 'Java', icon: FaJava },
      { name: 'Assembly', icon: FaTerminal },
      { name: 'MATLAB', icon: TbMathFunction },
      { name: 'VHDL', icon: TbCircuitGround },
    ],
  },
  {
    title: 'Frameworks & Libraries',
    items: [
      { name: 'CUDA', icon: SiNvidia },
      { name: 'OpenGL', icon: SiOpengl },
      { name: 'OpenMP', icon: TbCpu },
      { name: 'OpenMPI', icon: TbCpu },
      { name: 'TensorFlow', icon: SiTensorflow },
      { name: 'Keras', icon: SiKeras },
      { name: 'OpenCV', icon: SiOpencv },
      { name: 'PlatformIO', icon: SiPlatformio },
      { name: 'CMake', icon: SiCmake },
      { name: 'RTOS', icon: TbClockBolt },
    ],
  },
  {
    title: 'Microcontrollers & Boards',
    items: [
      { name: 'Teensy', icon: FaMicrochip },
      { name: 'ESP32', icon: SiEspressif },
      { name: 'Raspberry Pi', icon: FaRaspberryPi },
      { name: 'Arduino', icon: SiArduino },
      { name: 'ARM Cortex-M', icon: FaMicrochip },
      { name: 'STM32', icon: FaMicrochip },
    ],
  },
  {
    title: 'Protocols & Networking',
    items: [
      { name: 'UART', icon: FaExchangeAlt },
      { name: 'I2C', icon: FaExchangeAlt },
      { name: 'SPI', icon: FaExchangeAlt },
      { name: 'CAN', icon: FaExchangeAlt },
      { name: 'Ethernet', icon: TbNetwork },
      { name: 'TCP', icon: TbNetwork },
      { name: 'UDP', icon: TbNetwork },
    ],
  },
];

const focusAreas = [
  'Firmware & Real-Time Systems',
  'Embedded Architecture',
  'Robotics & Autonomy',
  'Low-Level Software',
  'GPU Compute',
];

const About = () => {
  const headingRef = useScrollReveal();
  const bodyRef = useScrollReveal();
  const specRef = useScrollReveal();
  const skillsRef = useScrollReveal();

  return (
    <section
      id="about"
      className="relative bg-bg text-ink py-28 overflow-hidden"
    >
      <div className="relative container mx-auto px-6">
        <div ref={headingRef} className="reveal max-w-4xl">
          <span className="eyebrow">01 / About</span>
          <h2 className="section-title mt-6 text-4xl sm:text-6xl md:text-7xl">
            About<span className="text-accent">.</span>
          </h2>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-12">
          <div ref={bodyRef} className="reveal-left lg:col-span-7">
            <p className="text-ink text-lg md:text-xl leading-relaxed">
              Computer Engineering student at{' '}
              <span className="text-accent">Georgia Tech</span>. I work on
              low-level systems like firmware, embedded architecture, and
              robotics. I enjoy building autonomous platforms and highly
              efficient, physical technologies.
            </p>
          </div>

          <div
            ref={specRef}
            className="reveal-right lg:col-span-5 flex flex-col gap-3"
          >
            <div className="mono-caption">Focus</div>
            <ul className="space-y-0 border-t border-line">
              {focusAreas.map((area) => (
                <li
                  key={area}
                  className="flex items-center justify-between border-b border-line py-3 text-ink hover:text-accent transition-colors"
                >
                  <span>{area}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div ref={skillsRef} className="reveal mt-28">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="eyebrow">02 / Skills</span>
              <h3 className="section-title mt-4 text-3xl sm:text-5xl text-ink">
                What I work with<span className="text-accent">.</span>
              </h3>
            </div>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            {skillGroups.map((group) => (
              <div
                key={group.title}
                className="border border-line bg-surface"
              >
                <div className="flex items-center justify-between px-5 py-3 border-b border-line">
                  <span className="text-ink font-display font-semibold">
                    {group.title}
                  </span>
                  <span className="mono-caption">{group.items.length}</span>
                </div>
                <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.name}
                        className="flex items-center gap-3 border border-line bg-bg px-3 py-2.5 hover:border-accent transition-colors"
                      >
                        <Icon className="text-lg text-accent flex-shrink-0" />
                        <span className="text-sm font-mono text-ink truncate">
                          {item.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
